import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { sidebarOptions, SidebarOption, getEffectiveSidebarOption } from '../../Data/SiderbarOptionsData'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAppSelector } from '../../Redux/Hooks'
import sendFirebaseEvent from '../../analytics/FirebaseAnalytics'
import kwikmediaCollapsedLogo from '../../assets/Icons/kwikmedia.webp'
import { getWhiteLableObject } from '../../config/whitelable'
import { device } from '../atoms/Utlis/media'
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from 'lucide-react'
import { trackClarityEvent } from '../../analytics/ClarityAnalytics'
import { CLARITY_EVENTS } from '../../analytics/EventTypes'
import { usePermissions } from '../../hooks/usePermissions'

const gradient =
  'linear-gradient(144.01deg, rgba(255, 201, 81, 0.85) 0%, rgba(206, 86, 176, 0.85) 100%)'

const isPathMatching = (current: string, target: string) => {
  if (!current || !target) return false
  const normCurrent = current.replace(/\/+$/, '') || '/'
  const normTarget = target.replace(/\/+$/, '') || '/'
  if (normTarget === '/dashboard') {
    return normCurrent === '/dashboard'
  }
  return normCurrent === normTarget || normCurrent.startsWith(normTarget + '/')
}

const getActiveChildKey = (
  children: SidebarOption[],
  pathname: string
): string | null => {
  if (!children.length) return null
  const sorted = [...children].sort((a, b) => {
    const normA = (a.redirection || '').replace(/\/+$/, '')
    const normB = (b.redirection || '').replace(/\/+$/, '')
    return normB.length - normA.length
  })
  const match = sorted.find((child) => isPathMatching(pathname, child.redirection))
  return match ? match.key : null
}

const Sidebar: React.FC<{ isExpand: boolean; toggelExpand: () => void }> = ({
  isExpand,
  toggelExpand,
}) => {
  const [activeTab, setActiveTab] = useState(sidebarOptions[0])
  const [openDropdown, setOpenDropdown] = useState<string | null>(null)

  const { currentActivePlan } = useAppSelector((state) => state.auth)
  const navigate = useNavigate()
  const {
    CalendarEventFlag,
    Package,
    UserEvent,
    UserTransaction,
    Quotation,
    WalletManagement,
    CustomerPaymentFlag,
    WhiteLabelWebsite,
    Orders,
    WhatsApp,
    Leads,
  } = useAppSelector((state) => state.flags)
  const location = useLocation()

  const currentPath = location.pathname

  const handleClick = (clickedTab: any) => {
    if (clickedTab.children?.length) {
      setOpenDropdown((current) =>
        current === clickedTab.key ? null : clickedTab.key
      )
      return
    }

    sendFirebaseEvent(
      'Sidebar',
      'User_Interaction',
      'Button_click',
      clickedTab.key
    )
    trackClarityEvent(CLARITY_EVENTS.SIDEBAR_ITEM_CLICKED, {
      itemKey: clickedTab.key,
      itemPath: clickedTab.redirection,
    })
    setActiveTab(clickedTab)
    navigate(clickedTab.redirection)
  }

  const { isStudioOwner, hasPermission, hasAnyPermission, hasModule, permissionsLoading } = usePermissions()

  const checkFlag = (key: string) => {
    if (key === 'Events') {
      return !!(CalendarEventFlag || UserEvent)
    } else if (key === 'CalendarEvent') {
      if (CalendarEventFlag) return true
      else return false
    } else if (key === 'Package') {
      return Package
    } else if (key === 'UserEvent') {
      return UserEvent
    } else if (key === 'UserTransaction') {
      return UserTransaction
    } else if (key === 'Quotation') {
      return Quotation
    } else if (key === 'Orders') {
      return Orders
    } else if (key === 'WalletManagement') {
      return WalletManagement
    } else if (key === 'CustomerPaymentFlag') {
      return CustomerPaymentFlag
    } else if (key === 'StudioWebsite') {
      return (
        currentActivePlan?.plan_name === 'Elite' ||
        (currentActivePlan?.plan_name === 'Business' && WhiteLabelWebsite)
      )
    } else if (key === 'WhatsApp') {
      return WhatsApp
    } else if (key === 'Leads') {
      return Leads
    } else if (key === 'RoleManagement') {
      return true
    } else return true
  }

  const isChildVisible = (opt: SidebarOption): boolean => {
    if (!checkFlag(opt.flag)) return false
    if (isStudioOwner) return true
    if (opt.key === 'Wallet Management' || opt.key === 'Studio Website') {
      return false
    }

    const hasPerm = opt.anyPermission
      ? opt.anyPermission.some((p) => hasPermission(p))
      : opt.permission
      ? hasPermission(opt.permission)
      : true

    const hasMod = opt.anyModule
      ? opt.anyModule.some((m) => hasModule(m))
      : opt.module
      ? hasModule(opt.module)
      : true

    if (opt.permission || opt.anyPermission) {
      if (!hasPerm && (!opt.module && !opt.anyModule || !hasMod)) return false
    }
    if ((opt.module || opt.anyModule) && !hasMod) return false

    return true
  }

  const isOptionVisible = (opt: SidebarOption): boolean => {
    if (!checkFlag(opt.flag)) return false
    if (isStudioOwner) return true
    if (opt.key === 'Wallet Management' || opt.key === 'Studio Website') {
      return false
    }

    if (opt.children && opt.children.length > 0) {
      const anyChildVisible = opt.children.some((child) => isChildVisible(child))
      if (!anyChildVisible) return false
      return true
    }

    return isChildVisible(opt)
  }

  useEffect(() => {
    for (const rawOption of sidebarOptions) {
      const Option = getEffectiveSidebarOption(
        rawOption,
        hasPermission,
        hasModule,
        isStudioOwner
      )
      const sortedChildren = Option.children
        ? [...Option.children].sort(
          (a, b) => b.redirection.length - a.redirection.length
        )
        : []
      const activeChild = sortedChildren.find((child) =>
        isPathMatching(currentPath, child.redirection)
      )
      if (activeChild) {
        setActiveTab(activeChild)
        setOpenDropdown(Option.key)
        return
      } else if (isPathMatching(currentPath, Option.redirection)) {
        setActiveTab(Option)
        setOpenDropdown(Option.children?.length ? Option.key : null)
        return
      }
    }
  }, [currentPath])

  const renderIcon = (
    option: (typeof sidebarOptions)[number],
    isActive: boolean
  ) => (
    <IconContainer $active={isActive}>
      <NavIcon src={isActive ? option.whiteIcon : option.icon} alt="" />
    </IconContainer>
  )

  return (
    <SidebarContainer $isExpand={isExpand}>
      <SidebarEdge aria-hidden />
      <ActionSidebarContainer>
        <ToggleButton
          type="button"
          onClick={() => toggelExpand()}
          aria-label={isExpand ? 'Collapse sidebar' : 'Expand sidebar'}
        >
          {isExpand ? (
            <ChevronRight size={14} color="white" strokeWidth={2.5} />
          ) : (
            <ChevronLeft size={14} color="white" strokeWidth={2.5} />
          )}
        </ToggleButton>
      </ActionSidebarContainer>

      <LogoContainer isExpand={isExpand}>
        {isExpand ? (
          <CompanyLogoImage
            isExpand={isExpand}
            src={`/logos/${getWhiteLableObject().logo.filename}`}
            alt={getWhiteLableObject().name}
          />
        ) : (
          <CompanyLogoImage
            isExpand={isExpand}
            src={kwikmediaCollapsedLogo}
            alt={getWhiteLableObject().name}
          />
        )}
      </LogoContainer>

      <SidebarOptionsContainer $isExpand={isExpand}>
        {sidebarOptions.map((rawOption) => {
          const option = getEffectiveSidebarOption(
            rawOption,
            hasPermission,
            hasModule,
            isStudioOwner
          )
          const visibleChildren = option.children?.filter(isOptionVisible) || []
          const isParentActive =
            isPathMatching(currentPath, option.redirection) ||
            !!visibleChildren.some((child) =>
              isPathMatching(currentPath, child.redirection)
            )
          const isOpen = openDropdown === option.key

          return isExpand
            ? isOptionVisible(option) && (
              <OptionGroup key={option.key}>
                <OptionContainer
                  $isExpanded={isExpand}
                  $isActive={isParentActive}
                  onClick={() => handleClick(option)}
                >
                  <OptionContent>
                    {renderIcon(option, isParentActive)}
                    <OptionText $isActive={isParentActive}>
                      {option.key}
                    </OptionText>
                  </OptionContent>
                  {visibleChildren.length ? (
                    <DropdownIcon
                      $isActive={isParentActive}
                      onClick={(e) => {
                        e.stopPropagation()
                        setOpenDropdown((current) =>
                          current === option.key ? null : option.key
                        )
                      }}
                    >
                      {isOpen ? (
                        <ChevronUp size={16} strokeWidth={2.2} />
                      ) : (
                        <ChevronDown size={16} strokeWidth={2.2} />
                      )}
                    </DropdownIcon>
                  ) : null}
                </OptionContainer>

                {isOpen && visibleChildren.length ? (
                  <ChildList>
                    {(() => {
                      const activeChildKey = getActiveChildKey(
                        visibleChildren,
                        currentPath
                      )
                      return visibleChildren.map((child) => {
                        const isChildActive = activeChildKey === child.key
                        return (
                          <ChildOptionContainer
                            key={child.key}
                            $isActive={isChildActive}
                            onClick={(e) => {
                              e.stopPropagation()
                              handleClick(child)
                            }}
                          >
                            <ChildDot $isActive={isChildActive} />
                            <ChildOptionText $isActive={isChildActive}>
                              {child.key}
                            </ChildOptionText>
                          </ChildOptionContainer>
                        )
                      })
                    })()}
                  </ChildList>
                ) : null}
              </OptionGroup>
            )
            : isOptionVisible(option) && (
              <OptionContainer
                key={option.key}
                $isExpanded={isExpand}
                $isActive={isParentActive}
                title={option.key}
                onClick={() =>
                  visibleChildren.length
                    ? handleClick(visibleChildren[0])
                    : handleClick(option)
                }
              >
                {renderIcon(option, isParentActive)}
              </OptionContainer>
            )
        })}
      </SidebarOptionsContainer>
    </SidebarContainer>
  )
}

export default Sidebar

const SidebarContainer = styled.div<{ $isExpand: boolean }>`
  width: fit-content;
  height: 100dvh;
  transition: all 0.2s ease-in-out;
  background: #fff;
  display: flex;
  flex-direction: column;
  position: relative;
  padding-top: ${(props) => (props.$isExpand ? '30px' : '18px')};
  gap: 24px;
  border-right: 1px solid rgba(23, 23, 23, 0.06);
  box-shadow: 4px 0 24px rgba(23, 23, 23, 0.03);

  @media ${device.tablet} {
    display: none;
  }
`

const SidebarEdge = styled.div`
  pointer-events: none;
  position: absolute;
  top: 0;
  right: 0;
  width: 2px;
  height: 100%;
  background: linear-gradient(
    180deg,
    rgba(255, 201, 81, 0.35) 0%,
    rgba(206, 86, 176, 0.25) 45%,
    transparent 100%
  );
  opacity: 0.7;
`

const ActionSidebarContainer = styled.div`
  position: absolute;
  right: -12px;
  top: 6px;
  z-index: 20;
`

const ToggleButton = styled.button`
  height: 24px;
  width: 24px;
  padding: 0;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${gradient};
  border-radius: 50%;
  box-shadow: 0 4px 12px rgba(201, 77, 170, 0.28);
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    transform: scale(1.06);
    box-shadow: 0 6px 16px rgba(201, 77, 170, 0.35);
  }
`

/* Logo placement + size intentionally unchanged */
const LogoContainer = styled.div<{ isExpand: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${(props) => (props.isExpand ? '4px' : '0px')};
  align-items: center;
  justify-content: center;
  transition: all 0.4s ease-in-out;
`

const CompanyLogoImage = styled.img<{ isExpand: boolean }>`
  width: ${(props) =>
    props.isExpand ? `${getWhiteLableObject().logo.width}` : '36px'};
  height: ${(props) =>
    props.isExpand ? `${getWhiteLableObject().logo.height}` : '36px'};
  object-fit: contain;
`

const SidebarOptionsContainer = styled.div.attrs({
  className: 'sidebar-scroll',
  'data-sidebar-scroll': 'true',
}) <{ $isExpand: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-grow: 1;
  overflow-y: auto;
  transition: all 0.3s ease-in-out;
  padding: ${(props) => (props.$isExpand ? '0 16px 24px' : '8px 10px 16px')};
  gap: 6px;

  /* Thin visible scrollbar — not the broad global style */
  scrollbar-width: thin !important;
  scrollbar-color: rgba(201, 77, 170, 0.35) transparent !important;

  &::-webkit-scrollbar {
    display: block !important;
    width: 3px !important;
    height: 3px !important;
    background: transparent !important;
  }

  &::-webkit-scrollbar-button {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
  }

  &::-webkit-scrollbar-track {
    background: transparent !important;
    border: none !important;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(201, 77, 170, 0.35) !important;
    border: none !important;
    border-radius: 999px !important;
    background-clip: border-box !important;
    box-shadow: none !important;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(201, 77, 170, 0.55) !important;
  }
`

const OptionGroup = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const OptionContainer = styled.div<{
  $isExpanded: boolean
  $isActive: boolean
}>`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  justify-content: ${(props) => (props.$isExpanded ? 'flex-start' : 'center')};
  padding: ${(props) => (props.$isExpanded ? '11px 12px' : '10px')};
  min-height: 44px;
  max-height: 50px;
  border-radius: 12px;
  cursor: pointer;
  overflow: hidden;
  background: ${(props) => (props.$isActive ? gradient : 'transparent')};
  box-shadow: ${(props) =>
    props.$isActive ? '0 6px 16px rgba(201, 77, 170, 0.22)' : 'none'};
  transition: background 0.15s ease, box-shadow 0.15s ease;
  z-index: 0;

  &::after {
    content: '';
    position: absolute;
    right: -18px;
    top: 64%;
    transform: translateY(-50%);
    width: 52px;
    height: 52px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.16);
    opacity: ${(props) => (props.$isActive ? 1 : 0)};
    pointer-events: none;
    z-index: 0;
  }

  > * {
    position: relative;
    z-index: 1;
  }

  &:hover {
    background: ${(props) =>
    props.$isActive ? gradient : 'rgba(201, 77, 170, 0.06)'};
  }
`

const OptionContent = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
`

const IconContainer = styled.div<{ $active?: boolean }>`
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(p) =>
    p.$active ? 'rgba(255, 255, 255, 0.18)' : 'rgba(23, 23, 23, 0.03)'};
`

const NavIcon = styled.img`
  height: 24px;
  width: 24px;
  object-fit: contain;
`

const OptionText = styled.p<{ $isActive: boolean }>`
  font-family: Urbanist, sans-serif;
  font-size: 14.5px;
  font-weight: ${(props) => (props.$isActive ? '700' : '550')};
  line-height: 1.25;
  text-align: left;
  margin: 0 0 0 12px;
  color: ${(props) => (props.$isActive ? '#fff' : '#171717')};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const DropdownIcon = styled.div<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 8px;
  flex-shrink: 0;
  color: ${(props) =>
    props.$isActive ? 'rgba(255,255,255,0.92)' : 'rgba(23, 23, 23, 0.45)'};
`

const ChildList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 2px 0 4px 10px;
  margin-left: 14px;
  border-left: 1.5px solid rgba(201, 77, 170, 0.16);
`

const ChildOptionContainer = styled.div<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 9px 10px;
  border-radius: 10px;
  cursor: pointer;
  background: ${(props) =>
    props.$isActive ? 'rgba(201, 77, 170, 0.08)' : 'transparent'};
  transition: background 0.15s ease;

  &:hover {
    background: ${(props) =>
    props.$isActive
      ? 'rgba(201, 77, 170, 0.1)'
      : 'rgba(23, 23, 23, 0.035)'};
  }
`

const ChildDot = styled.span<{ $isActive: boolean }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${(p) => (p.$isActive ? '#c94daa' : 'rgba(23, 23, 23, 0.22)')};
`

const ChildOptionText = styled.p<{ $isActive: boolean }>`
  margin: 0;
  font-family: Urbanist, sans-serif;
  font-size: 13.5px;
  font-weight: ${(props) => (props.$isActive ? '700' : '500')};
  line-height: 1.25;
  color: ${(props) => (props.$isActive ? '#c94daa' : '#4d4d4d')};
`
